import { PostOrPage } from '@tryghost/content-api';
import { Context, FunctionComponent, useContext, useMemo } from 'react';

import { RouteContext } from 'src/context';
import { useLatestRelease } from 'src/hooks/useLatestRelease';
import { useAppSelector } from 'src/hooks/useStore';
import { WhatsNewModal } from 'src/legacy/components/WhatsNew/WhatsNewModal';
import { useGetDismissedLatestReleaseQuery } from 'src/services/api/LatestReleaseApi';
import { selectCurrentUser } from 'src/store/storeUtils';
import { DateUtils } from 'src/utils';

type WhatsNewRouteContext = typeof RouteContext & {
  query: {
    /** `showLatestRelease` is a query param override to show the what's new modal.
     *
     * This will ignore the the dismissal state and also the time window
     * required to see the modal.
     *
     * Possible values:
     * - `true` - force show the latest release
     * - `false` - default behavior
     * - `hide` - force hide the latest release (used for autotesting overrides)
     */
    showLatestRelease: 'true' | 'false' | 'hide';
  };
};

export const isReleasePostOlderThan14Days = (
  latestReleasePost: PostOrPage | null,
) => {
  if (!latestReleasePost?.published_at) return true;

  const publishedDate = new Date(latestReleasePost.published_at);

  const hoursSinceRelease = DateUtils.getDiffFromDate(publishedDate, 'hours');
  return hoursSinceRelease > 14 * 24;
};

/**
 * The What's New modal component. Displayed if the latest release post
 * is available and the user can see it. It also handles the dismissal state
 * and the time window required to see the modal.
 */
export const WhatsNew: FunctionComponent = () => {
  const {
    query: { showLatestRelease = 'false' },
  } = useContext<WhatsNewRouteContext>(
    RouteContext as unknown as Context<WhatsNewRouteContext>,
  );
  const isClient = useAppSelector((state) => state.user.isClient);
  const currentUserCreatedDate = useAppSelector(selectCurrentUser)?.createdDate;

  const showLatestReleaseOverride = showLatestRelease === 'true';

  const userCanSeeWhatsNew =
    !isClient &&
    (showLatestReleaseOverride ||
      DateUtils.getDaysDiffOfDate(new Date(currentUserCreatedDate as string)) >
        7);

  const latestReleasePost = useLatestRelease(!userCanSeeWhatsNew);

  const {
    data: latestReleaseDismissal,
    isLoading: isLoadingDismissedLatestRelease,
  } = useGetDismissedLatestReleaseQuery(null, { skip: !userCanSeeWhatsNew });

  // this determines whether to show the latest release card or not
  // it actually compares the latest release post id with the latest release dismissal
  // state. If the latest release id is in dismissal object do not show it.
  // Also check for other conditions:
  // - The post is not older than 14 days
  // - The post has html content
  // - The post is not loading
  // - There are no relevant query params to override the behavior
  // - The user can see whats new
  // - The post is not dismissed
  const showLatestReleaseSection = useMemo<boolean>(() => {
    // post has no html content
    if (!latestReleasePost?.html) return false;
    // post is loading
    if (isLoadingDismissedLatestRelease) return false;

    // query param overrides
    if (showLatestRelease === 'hide') return false;
    if (showLatestReleaseOverride) return true;

    return (
      // user can see whats new
      userCanSeeWhatsNew &&
      // post is not dismissed
      latestReleaseDismissal?.structFields.latest_release_dismissed !==
        latestReleasePost?.id &&
      // post is not older than 14 days
      !isReleasePostOlderThan14Days(latestReleasePost)
    );
  }, [
    isLoadingDismissedLatestRelease,
    latestReleaseDismissal,
    latestReleasePost,
    showLatestRelease,
    showLatestReleaseOverride,
    userCanSeeWhatsNew,
  ]);

  return showLatestReleaseSection ? (
    <WhatsNewModal latestReleasePost={latestReleasePost} />
  ) : null;
};
